import { createRef, PureComponent } from 'react';
import cx from 'classnames';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import tinycolor from 'tinycolor2';
import SvgIcon from 'root/components/SvgIcon';
import Checkbox from 'root/components/Checkbox/Checkbox';
import PreviewInfo from 'root/components/PreviewInfo';
import { selectPalette } from '../../../selectors';
import GridTileSkeleton from './GridTileSkeleton';
import SVG_ICONS from '~/assets/icons/icons';

import './GridTile.scss';

const FILE_ICON_SIZE = 72;

const ID_STRIPE_HEIGHT = 7.5; // (Percentage)

export class GridTile extends PureComponent {
  state = {
    imgHeight: null,
    imgWidth: null,
  };

  componentDidMount() {
    if (this.isVideo() && this.videoRef.current) {
      this.videoRef.current.onloadeddata = () => {
        if (this.videoRef.current && this.videoRef.current.classList) {
          this.videoRef.current.classList.remove('hidden');
        }
      };
    }
  }

  onImageLoaded = (e) => {
    e.target.classList.remove('hidden');

    if (this.props.useImageClientSize && this.imgRef.current) {
      this.setState({
        imgHeight: this.imgRef.current.clientHeight,
        imgWidth: this.imgRef.current.clientWidth,
      });
    }
  };

  onImageError = (e) => {
    if (e.target.parentElement) {
      e.target.parentElement.classList.add('img-fail');
    }
  };

  onSelectorClick = (e) => {
    const { selectionAction, info } = this.props;
    e.stopPropagation();

    if (selectionAction) {
      selectionAction(info.id, e);
    }
  };

  getTileBgColor = (colorFromPalette) => {
    const bg = tinycolor(colorFromPalette);
    const hsl = bg.toHsl();
    hsl.l = hsl.l > 0.5 ? hsl.l - 0.1 : hsl.l + 0.1;

    return tinycolor(hsl).toHslString();
  };

  isVideo = () => {
    let isVideo;
    if (this.props.info.isVideo) {
      return true;
    }

    if (this.props.info.thumbnail && this.props.info.thumbnail.split('?')[0]) {
      const match = this.props.info.thumbnail.split('?')[0].match(/\.[0-9a-z]+$/i); // TODO: why is this needed??????

      isVideo = match ? Boolean(match[0].match(/\.(mp4|mov)/)) : false;
    }

    return isVideo;
  };

  videoRef = createRef();

  imgRef = createRef();

  imgContainerRef = createRef();

  playVideo = () => {
    if (this.videoRef.current) {
      if (this.videoRef.current.ended) {
        // Video has ended so restart it.
        this.videoRef.current.currentTime = 0;
      }
      this.videoRef.current.play();
    }
  };

  pauseVideo = () => {
    if (this.videoRef.current) {
      this.videoRef.current.pause();
    }
  };

  renderSelector = () => {
    const { isSelected, allowSelect } = this.props;

    const checkboxWrapCls = cx('grid-checkbox', 'absolute', 'top-0 left-0', 'ma2 ma3-ns', { dn: !isSelected });

    return (
      allowSelect && (
        <div className={checkboxWrapCls}>
          <Checkbox
            checked={isSelected}
            borderWidth={2}
            borderRadius={2}
            borderColor="#FFFFFF"
            size={20}
            onClick={(e) => {
              this.onSelectorClick(e);
            }}
          />
        </div>
      )
    );
  };

  renderThumbnail = () => {
    const { info } = this.props;
    const { imgHeight, imgWidth } = this.state;

    let thumb = <SvgIcon path={info.isFolder ? SVG_ICONS.FOLDER : SVG_ICONS.FILE} size={FILE_ICON_SIZE} colorName="source-bg" />;
    if (info.thumbnail) {
      if (this.isVideo()) {
        thumb = (
          <video className="hidden w-100 h-100" ref={this.videoRef}>
            <source src={info.thumbnail} type="video/mp4" />
          </video>
        );
      } else {
        thumb = (
          <div
            className={cx('overflow-hidden w-100 h-100', {
              'flex flex-column justify-center': !imgHeight,
            })}
            ref={this.imgContainerRef}
            style={
              imgHeight
                ? {
                    height: imgHeight - (imgHeight * ID_STRIPE_HEIGHT) / 100,
                    width: imgWidth,
                  }
                : {}
            }
          >
            <img
              className="hidden mw-100 mh-100"
              src={info.thumbnail}
              onLoad={this.onImageLoaded}
              onError={this.onImageError}
              ref={this.imgRef}
              style={{
                maxHeight: imgHeight ? 'initial' : '100%',
              }}
            />
          </div>
        );
      }
    }

    const cls = cx('absolute', 'top-0', 'left-0', 'w-100', 'h-100', 'flex', 'justify-center', 'items-center');

    return (
      <div className={cls} onClick={this.onSelectorClick}>
        {thumb}
      </div>
    );
  };

  renderContent = () => (
    <>
      {this.renderSelector()}
      {this.renderThumbnail()}
      <PreviewInfo {...this.props.info} onSelectorClick={this.onSelectorClick}>
        {this.props.customPreviewInfo}
      </PreviewInfo>
    </>
  );

  render() {
    const { isSelected, className, palette, tileNsSize, isLoading } = this.props;
    const tileCls = cx(
      'grid-tile',
      'tile',
      'f-l',
      'pr3 pr4-ns',
      'pb3 pb4-ns',
      'w-50',
      `w-${tileNsSize || '25'}-ns`,
      { selected: isSelected },
      { [`${className || ''}`]: !!className }
    );

    const cls = 'GridTile relative pointer relative';
    const backgroundColor = this.getTileBgColor(palette.sourceBg);

    return (
      <li
        className={cx(tileCls, {
          'always-show-grid-info': this.props.alwaysShowPreviewInfo,
        })}
        style={{ pointerEvents: isLoading ? 'none' : 'auto' }}
        data-test="grid-tile"
      >
        {isLoading ? (
          <div className={cls}>
            <GridTileSkeleton />
          </div>
        ) : this.isVideo() ? (
          <div
            className={cls}
            style={{ backgroundColor }}
            onMouseEnter={this.playVideo}
            onMouseLeave={this.pauseVideo}
            onTouchStart={this.playVideo}
            onTouchEnd={this.pauseVideo}
          >
            {this.renderContent()}
          </div>
        ) : (
          <div className={cls} style={{ backgroundColor }}>
            {this.renderContent()}
          </div>
        )}
      </li>
    );
  }
}

export default connect(
  createStructuredSelector({
    palette: selectPalette,
  })
)(GridTile);
